import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX } = FiIcons;

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);

  const images = [
    { src: 'https://i.ibb.co/pjkTZ8JM/Ossington-Barber-Shop-image-1.jpg', alt: 'Barber Shop Interior' },
    { src: 'https://i.ibb.co/TqBBnPyG/Ossington-Barber-Shop-image-2.jpg', alt: 'Professional Haircut' },
    { src: 'https://i.ibb.co/21B6wZBk/Ossington-Barber-Shop-image-3.jpg', alt: 'Classic Styling' },
    { src: 'https://i.ibb.co/HpVR3z32/Ossington-Barber-Shop-image-4.jpg', alt: 'Beard Trimming' },
    { src: 'https://i.ibb.co/Q7nb0Xhn/Ossington-Barber-Shop-image-5.jpg', alt: 'Modern Cuts' },
    { src: 'https://i.ibb.co/B2M2PMW4/Ossington-Barber-Shop-image-6.jpg', alt: 'Professional Service' },
    { src: 'https://i.ibb.co/HD3W85H0/Ossington-Barber-Shop-image-7.jpg', alt: 'Shop Atmosphere' },
    { src: 'https://i.ibb.co/jP3TcWw0/Ossington-Barber-Shop-image-8.jpg', alt: 'Quality Work' },
    { src: 'https://i.ibb.co/4Rd8WtXh/Ossington-Barber-Shop-image-9.jpg', alt: 'Expert Styling' },
    { src: 'https://i.ibb.co/xr7XRfn/Ossington-Barber-Shop-image-10.jpg', alt: 'Premium Service' },
    { src: 'https://i.ibb.co/j9tRTVVX/Ossington-Barber-Shop-image-12.jpg', alt: 'Professional Results' },
    { src: 'https://i.ibb.co/tTSFhTXv/Ossington-Barber-Shop-image-13.jpg', alt: 'Barber Excellence' },
    { src: 'https://i.ibb.co/j9ZtzTTn/Ossington-Barber-Shop-image-14.jpg', alt: 'Style Innovation' },
    { src: 'https://i.ibb.co/yB82kxw9/Ossington-Barber-Shop-image-15.jpg', alt: 'Customer Satisfaction' }
  ];

  const openModal = (image) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <section className="bg-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Gallery
          </motion.h1>
          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-lg md:text-xl max-w-3xl mx-auto"
          >
            Take a look at our work and see the quality and craftsmanship that sets us apart. From classic cuts to modern styles, we deliver excellence every time.
          </motion.p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {images.map((image, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                viewport={{ once: true }}
                className="aspect-square overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => openModal(image)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
            className="relative max-w-4xl max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white hover:text-red-400 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-8 h-8" />
            </button>
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4 text-lg">
              {selectedImage.alt}
            </p>
          </motion.div>
        </motion.div>
      )}

      {/* Call to Action */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Look This Good?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Book your appointment today and let our skilled barbers give you the perfect cut that matches your style and personality.
            </p>
            <a
              href="tel:+14165327560"
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 inline-block text-lg"
            >
              Book Your Appointment
            </a>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}

export default Gallery;